import React, { useMemo } from "react";
import { Autocomplete, TextField, Popper } from "@mui/material";
import { getBudgetTypeInfo } from "../../utils/budgetTypeUtils";
import { useTheme } from "../../ThemeContext";

/**
 * Reusable Searchable Autocomplete Project Dropdown
 * Placed in src/components/common for application-wide reuse.
 *
 * Used across:
 * 1. ManualEntryModal (creating manual invoice)
 * 2. ContractMappingModal (mapping contract projects)
 * 3. FixedCostMappingModal (mapping fixed cost projects)
 */
const ProjectSelectAutocomplete = ({
  projects = [],
  value,
  onChange,
  disabled = false,
  disableClearable = true,
  placeholder = "Select and search project...",
  error = false,
  helperText = "",
  existingProjectsMap = {},
  isDarkMode: propIsDarkMode,
  sx = {},
  fullWidth = true,
  id = "project-autocomplete",
}) => {
  const { isDarkMode: contextIsDarkMode } = useTheme();
  const isDarkMode = propIsDarkMode !== undefined ? propIsDarkMode : contextIsDarkMode;

  // Sort projects alphabetically (A to Z)
  const sortedProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return [...projects].sort((a, b) => {
      const titleA = a?.title || a?.project_name || a?.name || "";
      const titleB = b?.title || b?.project_name || b?.name || "";
      return titleA.localeCompare(titleB, undefined, { sensitivity: "base" });
    });
  }, [projects]);

  // Resolve selected project object from value (ID, title, or object)
  const selectedObj = useMemo(() => {
    if (!value && value !== 0) return null;
    if (typeof value === "object" && value !== null) {
      return value;
    }
    const valStr = String(value);
    return (
      sortedProjects.find(
        (p) =>
          String(p.project_id || p.id) === valStr ||
          p.title === value ||
          p.project_name === value ||
          p.name === value
      ) || null
    );
  }, [value, sortedProjects]);

  const budgetInfo = getBudgetTypeInfo(selectedObj?.budget_type);
  const existingInfo = selectedObj
    ? existingProjectsMap[String(selectedObj.project_id || selectedObj.id)] ||
      existingProjectsMap[(selectedObj.title || selectedObj.project_name || selectedObj.name || "").toLowerCase()]
    : null;
  const existingStatus = (existingInfo?.status?.toLowerCase() === "allocated" || existingInfo?.status?.toLowerCase() === "mapped") ? existingInfo?.status : null;
  const statusBadgeClass =
    existingStatus?.toLowerCase() === "allocated"
      ? "badge-status-allocated"
      : "badge-status-mapped";

  return (
    <Autocomplete
      id={id}
      options={sortedProjects}
      disabled={disabled}
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      getOptionLabel={(option) => {
        if (!option) return "";
        return option.title || option.project_name || option.name || `Project #${option.project_id || option.id}`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return String(option.project_id || option.id) === String(val.project_id || val.id);
      }}
      value={selectedObj}
      onChange={(_, newValue) => {
        if (onChange) {
          const newId = newValue ? (newValue.project_id ?? newValue.id) : "";
          onChange(newId, newValue);
        }
      }}
      noOptionsText={
        <span className={isDarkMode ? "text-white" : "text-secondary"}>
          No options
        </span>
      }
      componentsProps={{
        paper: {
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e !important" : "#ffffff !important",
            color: isDarkMode ? "#ffffff !important" : "#1f2937 !important",
            border: isDarkMode ? "1px solid #333 !important" : "1px solid #e5e7eb !important",
            "& .MuiAutocomplete-noOptions": {
              color: isDarkMode ? "#ffffff !important" : "#6b7280 !important",
              backgroundColor: isDarkMode ? "#1e1e1e !important" : "#ffffff !important",
              fontSize: "14px",
              padding: "10px 14px",
            },
          },
        },
      }}
      PopperComponent={(popperProps) => (
        <Popper
          {...popperProps}
          placement="bottom-start"
          modifiers={[
            {
              name: "flip",
              enabled: true,
              options: {
                fallbackPlacements: ["top-start", "bottom-start"],
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
          ]}
          sx={{
            zIndex: 1700,
            "& .MuiAutocomplete-paper": {
              backgroundColor: isDarkMode ? "#1e1e1e !important" : "#ffffff !important",
              color: isDarkMode ? "#ffffff !important" : "#1f2937 !important",
              border: isDarkMode ? "1px solid #333 !important" : "1px solid #e5e7eb !important",
            },
            "& .MuiAutocomplete-noOptions": {
              color: isDarkMode ? "#ffffff !important" : "#6b7280 !important",
              backgroundColor: isDarkMode ? "#1e1e1e !important" : "#ffffff !important",
              fontSize: "14px",
              padding: "10px 14px",
            },
          }}
        />
      )}
      ListboxProps={{
        sx: {
          maxHeight: "260px !important",
          overflowY: "auto",
          p: 0,
          backgroundColor: isDarkMode ? "#1e1e1e !important" : "#ffffff !important",
          color: isDarkMode ? "#f3f4f6 !important" : "#1f2937 !important",
          border: isDarkMode ? "1px solid #333 !important" : "1px solid #e5e7eb !important",
        },
      }}
      renderOption={(props, proj) => {
        const val = proj.project_id || proj.id;
        const title = proj.title || proj.project_name || proj.name || `Project #${val}`;
        const bInfo = getBudgetTypeInfo(proj.budget_type);

        const exInfo =
          existingProjectsMap[String(val)] ||
          existingProjectsMap[title.toLowerCase()];
        const exStatus = (exInfo?.status?.toLowerCase() === "allocated" || exInfo?.status?.toLowerCase() === "mapped") ? exInfo?.status : null;
        const hasEx = Boolean(exStatus);

        const stBadgeClass =
          exStatus?.toLowerCase() === "allocated"
            ? "badge-status-allocated"
            : "badge-status-mapped";

        return (
          <li
            {...props}
            key={val}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              padding: "8px 14px",
              cursor: "pointer",
              fontSize: "14px",
              borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f8fafc",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                flex: 1,
                marginRight: "8px",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: isDarkMode ? "#f3f4f6" : "#1f2937",
                  fontWeight: 500,
                }}
              >
                {title}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexShrink: 0,
              }}
            >
              {bInfo.badgeLabel && (
                <span className={bInfo.badgeClass}>
                  {bInfo.badgeLabel}
                </span>
              )}
              {hasEx && (
                <span
                  className={stBadgeClass}
                  style={{
                    fontSize: "11px",
                    height: "20px",
                    lineHeight: "20px",
                    padding: "0 8px",
                    fontWeight: 600,
                  }}
                >
                  {exStatus}
                </span>
              )}
            </div>
          </li>
        );
      }}
      renderInput={(inputParams) => (
        <TextField
          {...inputParams}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...inputParams.InputProps,
            endAdornment: (
              <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                {selectedObj && budgetInfo.badgeLabel && (
                  <span
                    className={budgetInfo.badgeClass}
                    style={{ pointerEvents: "none", whiteSpace: "nowrap" }}
                  >
                    {budgetInfo.badgeLabel}
                  </span>
                )}
                {selectedObj && existingStatus && (
                  <span
                    className={statusBadgeClass}
                    style={{
                      fontSize: "11px",
                      height: "20px",
                      lineHeight: "20px",
                      padding: "0 8px",
                      fontWeight: 600,
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                      marginLeft: "4px",
                    }}
                  >
                    {existingStatus}
                  </span>
                )}
                {disabled ? null : inputParams.InputProps.endAdornment}
              </div>
            ),
          }}
          sx={{
            "& .MuiAutocomplete-endAdornment": {
              position: "static",
              transform: "none",
              display: "inline-flex",
              alignItems: "center",
              marginLeft: "3px",
              flexShrink: 0,
            },
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
              paddingRight: "8px !important",
              backgroundColor: disabled
                ? (isDarkMode ? "#1e293b" : "#f8f9fa")
                : (isDarkMode ? "#1e293b" : "#fff"),
              color: isDarkMode ? "#f2f1f1" : "#1e293b",
              cursor: disabled ? "default" : "text",
              ...(disabled && {
                "& fieldset": {
                  borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                },
                "& .MuiOutlinedInput-input.Mui-disabled": {
                  WebkitTextFillColor: isDarkMode ? "#f2f1f1" : "#1e293b",
                  cursor: "default",
                },
              }),
              ...(isDarkMode && !disabled && {
                "& fieldset": {
                  borderColor: "#475569",
                },
              }),
              ...(error && {
                "& fieldset": {
                  borderColor: "#dc2626 !important",
                },
              }),
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              color: isDarkMode ? "#f2f1f1" : "#1e293b",
            },
            ...sx,
          }}
        />
      )}
    />
  );
};

export default React.memo(ProjectSelectAutocomplete);
